"use client";

import { IconClear, MobileProfileForm } from "@/components";
import MobileAuth from "@/components/AuthForms/MobileAuth";
import { useAuth } from "@/contexts/AuthContext";
import { FORM_TYPES } from "@/utils/constants";
import Link from "next/link";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const MobileLandingPageStickyNav: React.FC = () => {
  const [showAuthView, setShowAuthView] = useState(false);
  const [showProfileView, setShowProfileView] = useState(false);
  const [authMode, setAuthMode] = useState<typeof FORM_TYPES[keyof typeof FORM_TYPES] | null>(null);
  const [mounted, setMounted] = useState(false);
  const { isAuthenticated, user } = useAuth();

  // Ensure we're mounted on client side
  useEffect(() => {
    setMounted(true);
  }, []);

  const handleLoginClick = () => {
    setAuthMode(FORM_TYPES.LOGIN);
    setShowAuthView(true);
  };

  const handleProfileClick = () => {
    setShowProfileView(true);
  };
  
  const handleCloseAuth = () => {
    setShowAuthView(false);
  };

  const handleCloseProfile = () => {
    setShowProfileView(false);
  };

  const handleAuthSuccess = () => {
    setShowAuthView(false);
  };

  const handleAuthModeChange = (mode: typeof FORM_TYPES[keyof typeof FORM_TYPES]) => {
    if (mode === FORM_TYPES.LOGIN || mode === FORM_TYPES.REGISTER) {
      setAuthMode(mode);
    }
  };

  // Render modals using portal to escape the sticky nav container
  const renderModals = () => {
    if (!mounted) return null;

    return (
      <>
        {/* Full-Screen Auth View */}
        {showAuthView && createPortal(
          <div className="fixed inset-0 z-[9999] bg-white flex flex-col md:hidden">
            {/* Header */}
            <div className="flex items-center justify-end px-4 py-6">
              <button onClick={handleCloseAuth} className="text-xl">
                <IconClear width={15} height={15} />
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-1 px-4 flex flex-col">
              <div className="flex border-b border-light-grey mb-6 font-raleway text-md">
                <button 
                  className={`px-2 pb-3 text-center ${
                    authMode === FORM_TYPES.LOGIN ? "border-b-2 border-black" : "text-gray-400"
                  }`}
                  onClick={() => setAuthMode(FORM_TYPES.LOGIN)}
                >
                  Login
                </button>
                <button 
                  className={`px-2 pb-3 text-center ${
                    authMode === FORM_TYPES.REGISTER ? "border-b-2 border-black" : "text-gray-400"
                  }`}
                  onClick={() => setAuthMode(FORM_TYPES.REGISTER)}
                >
                  Register
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <MobileAuth  
                  initialMode={authMode || FORM_TYPES.LOGIN}
                  onSuccess={handleAuthSuccess}
                  onModeChange={handleAuthModeChange}
                  showTabs={false}
                />
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Full-Screen Profile View */}
        {showProfileView && createPortal(
          <div className="fixed inset-0 z-[9999] bg-white flex flex-col md:hidden">
            {/* Header */}
            <div className="flex items-center justify-end px-4 py-6">
              <button onClick={handleCloseProfile} className="text-xl">
                <IconClear width={15} height={15} />
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-1 px-4 flex flex-col overflow-y-auto">
              <MobileProfileForm onClose={handleCloseProfile} />
            </div>
          </div>,
          document.body
        )}
      </>
    );
  };

  return (
    <>
      <div className="flex gap-6 items-center justify-between px-4 py-3">
        <Link
          href="/listings"
          className="flex-1 bg-white text-center border border-black text-black px-6 py-2 font-raleway text-sm font-bold tracking-[0.1em] transition-colors hover:bg-light-grey"
        >
          BAGS
        </Link>

        {isAuthenticated ? (
          <button
            onClick={handleProfileClick}
            className="flex-1 bg-white text-center text-black border border-black px-6 py-2 font-raleway text-sm font-bold tracking-[0.1em] transition-colors hover:bg-light-grey"
          >
            {user?.first_name?.toUpperCase() || "PROFILE"}
          </button>
        ) : (
          <button
            onClick={handleLoginClick}
            className="flex-1 bg-white text-black border border-black px-6 py-2 font-raleway text-sm font-bold tracking-[0.1em] transition-colors hover:bg-light-grey"
          >
            LOGIN
          </button>
        )}
      </div>

      {/* Render modals using portals */}
      {renderModals()}
    </>
  );
};

export default MobileLandingPageStickyNav;
